<template>
  <span
    ref="inputRef"
    :aria-label="label"
    :contenteditable="canEdit"
    :tabindex="canEdit ? 0 : -1"
    class="inline-text-input max-w-full rounded-md p-1 font-bold default:min-w-36"
    :class="{
      'focus:border-primary-500 cursor-pointer focus-within:cursor-text hover:bg-neutral-50 focus:border focus:bg-white':
        canEdit,
      block: multiline,
      'single-line line-clamp-1 text-ellipsis': !multiline,
    }"
    :data-placeholder="placeholder"
    @focus="onFocus"
    @blur="onBlur"
    @mousedown.stop
    @click.stop.prevent="onClick"
    @keydown.enter="onEnter"
  >
    {{ value }}
  </span>
</template>
<script lang="ts" setup>
import { ref } from 'vue';

const props = defineProps<{
  label: string;
  value: string | null;
  canEdit: boolean;
  placeholder?: string;
  multiline?: boolean;
  emptyName?: string;
  clearable?: boolean;
}>();

const emit = defineEmits<{ update: [string] }>();

const inputRef = ref<HTMLInputElement>();

// NOTE: tracking focus like this is required for use of this component inside an Editor
// The reason for this is that tiptap uses the `contenteditable` property to determine cursor jump behavior,
// so having this on all the time messes with navigation inside the editor, which is why we toggle the property conditionally
const isFocused = ref(false);

function updated() {
  if (!inputRef.value) return;

  inputRef.value.blur();

  if (!props.clearable && inputRef.value.textContent === '') {
    inputRef.value.textContent = props.value;
    return;
  }

  if (inputRef.value.textContent === props.value) {
    return;
  }

  emit('update', inputRef.value.textContent);
}

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
  updated();
}

function onClick() {
  if (!props.canEdit) return;

  // Focus the element to make it editable
  if (inputRef.value) {
    inputRef.value.focus();
  }

  blurIfEmpty();
}

function blurIfEmpty() {
  if (props.value && props.value === props.emptyName && inputRef.value) {
    inputRef.value.textContent = '';
  }
}

function onEnter(event: KeyboardEvent) {
  if (props.multiline) {
    return;
  }

  event.preventDefault();
  updated();
}
</script>

<style scoped>
.single-line {
  white-space: nowrap;
  overflow: hidden;
}
.single-line br {
  display: none;
}
.single-line * {
  display: inline;
  white-space: nowrap;
}

.inline-text-input {
  position: relative;
}
/* Render the placeholder out of flow so the caret sits at the START of the
   field (like a native input), not after the placeholder text. Offsets match
   the `p-1` padding; `max-width` keeps a long placeholder inside the box. */
.inline-text-input:empty::before {
  content: attr(data-placeholder);
  position: absolute;
  inset-inline-start: 0.25rem;
  top: 0.25rem;
  max-width: calc(100% - 0.5rem);
  pointer-events: none;
  color: var(--color-neutral-500);
  opacity: 0.5;
}
</style>
